import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import ReactCrop from 'react-image-crop';
import { confirmAlert } from 'react-confirm-alert';
import OutsideClickHandler from 'react-outside-click-handler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagic, faRepeat } from '@fortawesome/pro-light-svg-icons';
import 'react-image-crop/lib/ReactCrop.scss';
import 'react-image-crop/dist/ReactCrop.css';
import './ProductPreview.scss';

import Loader from '../../General/Loader';

import { prettifyImage as prettifyImageAPI, flipImage as flipImageAPI } from '../../../api/images';
import { updateProductImage } from '../../../api/products';
import { EditProductModal } from '../form/EditProductModal';
import { getBrandNameWithFallback } from '../form/lib/brandHelpers';
import { getShopProductUrl, getSmartImage } from '../../../helpers/helpers';

const IMAGE_SIZE = 200;
const DEFAULT_NUM_VISIBLE_IMAGES_TO_SHOW = 4;

class ProductPreview extends Component {
  static propTypes = {
    isEditing: PropTypes.bool.isRequired,
    updateCrop: PropTypes.func.isRequired,
    syncProduct: PropTypes.func.isRequired,
    updateImageUrl: PropTypes.func.isRequired,
    brand: PropTypes.object,
    title: PropTypes.string,
    image: PropTypes.string,
    category: PropTypes.object,
    product: PropTypes.object,
    onProductUpdate: PropTypes.func
  };

  componentDidUpdate(prevProps) {
    const prevTitle = _.get(prevProps, 'title');
    const curTitle = _.get(this.props, 'title');
    if (prevTitle !== curTitle) {
      this.setState({
        crop: { aspect: 1 },
        applyingCrop: false,
        appliedCrop: this.getStarterCrop()
      });
    }

    // If product changes, reset num Visible images
    const prevProduct = _.get(prevProps, 'product');
    const curProduct = _.get(this.props, 'product');
    if (!_.isEqual(prevProduct?.id, curProduct?.id)) {
      this.setState({ numVisibleImages: DEFAULT_NUM_VISIBLE_IMAGES_TO_SHOW });
    }
  }

  getStarterCrop = () => ({
    unit: 'px',
    x: 0,
    y: 0,
    aspect: 1,
    height: 0,
    width: 0
  });

  state = {
    crop: { aspect: 1 },
    applyingCrop: false,
    appliedCrop: this.getStarterCrop(),
    prettifyingImage: false,
    flippingImage: false,
    numVisibleImages: DEFAULT_NUM_VISIBLE_IMAGES_TO_SHOW
  };

  setCrop = crop => {
    this.setState({
      crop: {
        ...crop,
        aspect: 1
      },
      applyingCrop: !_.isEqual(crop, this.state.appliedCrop) && !_.isEqual(crop, this.getStarterCrop())
    });
  };

  applyCrop = crop => {
    this.appliedZoomOffset = this.zoomOffset;
    this.appliedXTransformOffset = this.xTransformOffset;
    this.appliedYTransformOffset = this.yTransformOffset;
    this.setState(
      {
        crop: this.getStarterCrop(),
        appliedCrop: this.state.crop,
        trueCrop: this.state.appliedCrop.width
          ? {
              width: this.state.crop.width / this.appliedZoomOffset,
              height: this.state.crop.width / this.appliedZoomOffset,
              x: (this.state.crop.x - this.xTransformOffset) / this.appliedZoomOffset,
              y: (this.state.crop.y - this.yTransformOffset) / this.appliedZoomOffset
            }
          : this.state.crop,
        applyingCrop: false
      },
      () => {
        this.props.updateCrop({
          x: this.state.trueCrop.x,
          y: this.state.trueCrop.y,
          width: this.state.trueCrop.width,
          height: this.state.trueCrop.height,
          frame: {
            width: IMAGE_SIZE
          }
        });
      }
    );
  };

  removeCrop = () => {
    this.zoomOffset = null;
    this.xTransformOffset = null;
    this.yTransformOffset = null;
    this.appliedZoomOffset = null;
    this.appliedXTransformOffset = null;
    this.appliedYTransformOffset = null;
    this.setState({
      crop: {},
      appliedCrop: this.getStarterCrop(),
      applyingCrop: false
    });
    this.props.updateCrop(null);
  };

  isCropApplied = () => !_.isEqual(this.getStarterCrop(), this.state.appliedCrop);

  getCroppedImageStyle = () => {
    const { width, height, x, y } = this.state.appliedCrop;

    if (height < 10 || width < 10) {
      return {};
    }

    const adjustedWidth = width / (this.appliedZoomOffset || 1);
    const adjustedHeight = height / (this.appliedZoomOffset || 1);

    const fullImageWidth = _.get(this.curImageDimensions, 'width', adjustedWidth);
    const fullImageHeight = _.get(this.curImageDimensions, 'height', adjustedHeight);

    const trueWidthRatio = fullImageWidth / adjustedWidth;
    const trueHeightRatio = fullImageHeight / adjustedHeight;

    const newImageWidth = trueWidthRatio * IMAGE_SIZE;
    const newImageHeight = trueHeightRatio * IMAGE_SIZE;

    const newImageOffsetX = (-1 * IMAGE_SIZE * (x - (this.appliedXTransformOffset || 0))) / adjustedHeight / (this.appliedZoomOffset || 1);
    const newImageOffsetY = (-1 * IMAGE_SIZE * (y - (this.appliedYTransformOffset || 0))) / adjustedHeight / (this.appliedZoomOffset || 1);

    this.zoomOffset = _.max([trueWidthRatio, trueHeightRatio]);
    this.xTransformOffset = newImageOffsetX;
    this.yTransformOffset = newImageOffsetY;
    return {
      width: `${newImageWidth.toFixed(2)}px`,
      maxWidth: `${newImageWidth.toFixed(2)}px`,
      height: `${newImageHeight.toFixed(2)}px`,
      maxHeight: `${newImageHeight.toFixed(2)}px`,
      transform: `translate(${newImageOffsetX.toFixed(1)}px, ${newImageOffsetY.toFixed(1)}px)`,
      objectFit: 'contain'
    };
  };

  onImageLoaded = image => {
    this.curImageDimensions = {
      width: image.width / (this.zoomOffset || 1),
      height: image.height / (this.zoomOffset || 1)
    };
  };

  flipImage = () => {
    const { image, updateImageUrl } = this.props;
    this.setState({ flippingImage: true });
    flipImageAPI(image)
      .then(resp => {
        updateImageUrl(resp.url);
      })
      .catch(error => {
        cogoToast.error(error || 'Had an error, check console.');
      })
      .finally(() => {
        this.setState({ flippingImage: false });
      });
  };
  prettifyImage = () => {
    const { image, updateImageUrl } = this.props;
    this.setState({ prettifyingImage: true });

    prettifyImageAPI(getSmartImage(image))
      .then(resp => {
        updateImageUrl(resp.url);
      })
      .catch(error => {
        cogoToast.error(error || 'Had an error, check console.');
      })
      .finally(() => {
        this.setState({ prettifyingImage: false });
      });
  };

  toggleImages = () => {
    this.setState({ viewingAllImages: !this.state.viewingAllImages });
  };

  render() {
    const { image, title, product, brand, category, isEditing } = this.props;
    const { crop, applyingCrop, prettifyingImage, flippingImage, numVisibleImages } = this.state;
    const images = _.get(product, 'images', []);

    const sortedImages = _.orderBy([...images], ['isFeatured', 'isCover', img => img === product.image], ['desc', 'desc', 'desc']);

    return (
      <div className='product-preview-outer-container'>
        <div className='product-preview'>
          {image ? (
            <OutsideClickHandler disabled={!applyingCrop} onOutsideClick={() => this.setState({ applyingCrop: false })}>
              <div className={cn('collection-product-img-wrapper', { editing: prettifyingImage || flippingImage })}>
                {prettifyingImage || flippingImage ? (
                  <div className='loading-container'>
                    <Loader />
                    <div className='message'>{flippingImage ? 'Flipping' : 'Prettifying'} Image</div>
                  </div>
                ) : (
                  <div className='transform-image-btns'>
                    <div onClick={this.flipImage} className='rotate-image-btn'>
                      <FontAwesomeIcon icon={faRepeat} />
                    </div>
                    <div onClick={this.prettifyImage} className='prettify-image-btn'>
                      <FontAwesomeIcon icon={faMagic} />
                    </div>
                  </div>
                )}
                <ReactCrop
                  imageStyle={this.getCroppedImageStyle()}
                  src={getSmartImage(image)}
                  crop={crop}
                  ruleOfThirds
                  onChange={this.setCrop}
                  onImageLoaded={this.onImageLoaded}
                />
              </div>
              <div className='product-image-controls'>
                {this.isCropApplied() && (
                  <div onClick={this.removeCrop} className='remove-crop-btn btn'>
                    RESET
                  </div>
                )}
                {applyingCrop && (
                  <div onClick={this.applyCrop} className='apply-crop-btn btn'>
                    APPLY
                  </div>
                )}
                {!applyingCrop && !this.isCropApplied() && <div className='apply-crop-msg'>Drag to crop</div>}
              </div>
            </OutsideClickHandler>
          ) : (
            <div className='empty image' />
          )}
          <div className='main-data'>
            <div className={cn('title', { empty: !title })}>{title}</div>
            <div className={cn('brand', { empty: !brand })}>{getBrandNameWithFallback(brand)}</div>
            <div className={cn('category', { empty: !category })}>{category && category.name}</div>
            <div className='pills'>
              {isEditing && product && <div className='in-catalog-badge pill'>ID: {product.id}</div>}
              {product && (
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href={getShopProductUrl(product.id)}
                  className='in-catalog-badge pill'
                >
                  View In Shop
                </a>
              )}
            </div>
            {product && <EditProductModal productId={product.id} className='edit-button' onSave={this.props.onProductUpdate} />}
          </div>
        </div>
        {!!product && (
          <div className='images-preview'>
            <div className='images'>
              {sortedImages.slice(0, numVisibleImages).map(imageData => {
                const { image, isFeatured, isCover } = imageData;
                const isProductImage = product.image === image;

                const clickImage = () => {
                  confirmAlert({
                    title: 'Update Image Data',
                    message:
                      'What would you like to do with this image? Featured images show up in product results, Cover images show up as the main image on the product page.',
                    buttons: [
                      {
                        label: 'Make Product Cover',
                        onClick: async () => {
                          await updateProductImage(imageData.id, {
                            isCover: true
                          });
                          this.props.syncProduct(product.id);
                        }
                      },
                      {
                        label: 'Cancel',
                        className: 'cancel',
                        onClick: () => {}
                      }
                    ]
                  });
                };

                return (
                  <div onClick={clickImage} key={image} className='image'>
                    <img src={getSmartImage(image)} alt={image} key={image} />
                    {isFeatured && isCover ? (
                      <div className='badge featured'>Featured + Cover{isProductImage ? '-p' : ''}</div>
                    ) : isFeatured ? (
                      <div className='badge featured'>Featured{isProductImage ? '-p' : ''}</div>
                    ) : isCover ? (
                      <div className='badge cover'>Cover{isProductImage ? '-p' : ''}</div>
                    ) : isProductImage ? (
                      <div className='badge product'>p</div>
                    ) : null}
                  </div>
                );
              })}
              {sortedImages.length > numVisibleImages && (
                <div onClick={() => this.setState({ numVisibleImages: numVisibleImages + 20 })} className='more-images'>
                  +{images.length - numVisibleImages} more
                </div>
              )}
              {numVisibleImages > DEFAULT_NUM_VISIBLE_IMAGES_TO_SHOW && (
                <div onClick={() => this.setState({ numVisibleImages: numVisibleImages - 20 })} className='less-images'>
                  Show Less
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ProductPreview;
